import React, { useRef, useState } from "react";
import { Paperclip, UploadSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";

import { mail as mailApi } from "@/api";
import ActaModal from "@/components/ui/ActaModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  DocumentAttachmentPicker,
  RecipientField,
} from "@/components/mail/MailDialogs";


function localDateTime(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString().slice(0, 16);
}


export default function MailScheduledEditDialog({ item, onClose, onSaved }) {
  const [draft, setDraft] = useState(() => ({
    to: (item.to || []).join(", "),
    cc: (item.cc || []).join(", "),
    bcc: (item.bcc || []).join(", "),
    subject: item.subject || "",
    body: item.text || "",
  }));
  const [scheduledAt, setScheduledAt] = useState(() => localDateTime(item.scheduledAt));
  const [kept, setKept] = useState(() => item.attachmentItems || []);
  const [newFiles, setNewFiles] = useState([]);
  const [documentPickerOpen, setDocumentPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const localInputRef = useRef(null);

  function addFiles(files) {
    const selected = Array.from(files || []);
    if (selected.length) setNewFiles((current) => [...current, ...selected]);
  }

  async function save(event) {
    event.preventDefault();
    if (saving) return;
    if (!draft.to.trim()) {
      toast.error("Renseignez un destinataire");
      return;
    }
    if (!draft.subject.trim() || !draft.body.trim()) {
      toast.error("L’objet et le contenu sont obligatoires");
      return;
    }
    if (newFiles.some((file) => !file || !Number.isFinite(file.size) || file.size <= 0)) {
      toast.error("Une pièce jointe est vide ou indisponible");
      return;
    }
    if (
      [...kept.map((file) => file.size), ...newFiles.map((file) => file.size)]
        .reduce((sum, size) => sum + size, 0) > 25 * 1024 * 1024
    ) {
      toast.error("Les pièces jointes dépassent 25 Mo");
      return;
    }

    const date = new Date(scheduledAt);
    if (!scheduledAt || Number.isNaN(date.getTime()) || date.getTime() <= Date.now() + 60000) {
      toast.error("Choisissez une date au moins une minute dans le futur");
      return;
    }

    const form = new FormData();
    form.append("expected_revision", String(item.revision));
    form.append("scheduled_at", date.toISOString());
    form.append("to", draft.to);
    form.append("cc", draft.cc);
    form.append("bcc", draft.bcc);
    form.append("subject", draft.subject);
    form.append("text", draft.body);
    form.append("keep_attachment_ids", JSON.stringify(kept.map((file) => file.id)));
    newFiles.forEach((file) => form.append("files", file));

    setSaving(true);
    try {
      await mailApi.updateScheduled(item.id, form);
      toast.success("Programmation modifiée");
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Modification impossible");
      if (error?.response?.status === 409) onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <ActaModal
        open
        size="xl"
        title="Modifier l’envoi planifié"
        subtitle="Seules les programmations encore en attente peuvent être modifiées."
        onClose={onClose}
        closeDisabled={saving}
        testId="mail-scheduled-edit"
      >
        <form onSubmit={save} className="space-y-3">
          <RecipientField field="to" placeholder="À" required draft={draft} setDraft={setDraft}
            onCheckRecipient={() => {}} onSearchRecipients={mailApi.recipientSuggestions} />
          <div className="grid gap-3 sm:grid-cols-2">
            <RecipientField field="cc" placeholder="Cc" draft={draft} setDraft={setDraft}
              onSearchRecipients={mailApi.recipientSuggestions} />
            <RecipientField field="bcc" placeholder="Cci" draft={draft} setDraft={setDraft}
              onSearchRecipients={mailApi.recipientSuggestions} />
          </div>
          <Input value={draft.subject} placeholder="Objet" required
            onChange={(event) => setDraft((current) => ({ ...current, subject: event.target.value }))} />
          <Textarea value={draft.body} placeholder="Message" required rows={7}
            onChange={(event) => setDraft((current) => ({ ...current, body: event.target.value }))} />
          <label className="block text-sm font-bold text-[var(--do-ink)]">
            Nouvelle date et heure
            <input type="datetime-local" required value={scheduledAt}
              onChange={(event) => setScheduledAt(event.target.value)}
              className="mt-1 block min-h-[44px] w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3"
              data-testid="mail-scheduled-edit-date" />
          </label>
          <div className="space-y-2 rounded-xl border border-[var(--do-border)] p-3">
            <p className="text-sm font-bold text-[var(--do-ink)]">Pièces jointes conservées</p>
            {!kept.length && <p className="text-xs text-[var(--do-muted)]">Aucune pièce jointe existante.</p>}
            {kept.map((file) => (
              <div key={file.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate"><Paperclip className="mr-1 inline" size={15} />{file.filename}</span>
                <button type="button" disabled={saving}
                  onClick={() => setKept((current) => current.filter((entry) => entry.id !== file.id))}
                  aria-label={`Retirer ${file.filename}`}><X size={17} /></button>
              </div>
            ))}
            {newFiles.map((file, index) => (
              <div key={`${file.name}-${index}`} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate">{file.name} (nouveau)</span>
                <button type="button" disabled={saving}
                  onClick={() => setNewFiles((current) => current.filter((_, i) => i !== index))}
                  aria-label={`Retirer ${file.name}`}><X size={17} /></button>
              </div>
            ))}
            <input ref={localInputRef} type="file" multiple className="sr-only"
              onChange={(event) => { addFiles(event.target.files); event.target.value = ""; }} />
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="actaSecondary" disabled={saving}
                onClick={() => localInputRef.current?.click()}>
                <UploadSimple size={16} /> Depuis l’appareil
              </Button>
              <Button type="button" variant="actaSecondary" disabled={saving}
                onClick={() => setDocumentPickerOpen(true)}>
                <Paperclip size={16} /> Depuis Documents
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <Button type="button" variant="actaSecondary" disabled={saving} onClick={onClose}>Retour</Button>
            <Button type="submit" variant="actaPrimary" disabled={saving}
              data-testid="mail-scheduled-edit-save">
              {saving ? "Enregistrement…" : "Enregistrer les modifications"}
            </Button>
          </div>
        </form>
      </ActaModal>
      <DocumentAttachmentPicker open={documentPickerOpen}
        onClose={() => setDocumentPickerOpen(false)} onAttach={addFiles} />
    </>
  );
}
